import {
  Box,
  Card,
  CardContent,
  Collapse,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { endOfMonth } from 'date-fns';
import { FC, useEffect, useMemo, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';

import { BetaChip } from '../../components/chips/BetaChip';
import { RechartTabelle } from '../../components/RechartTabelle';
import { useAppSelector } from '../../state/redux.hooks';
import { useLazyFindAllAufwandQuery } from '../aufwand/api/aufwandApi';
import { useMitarbeiterRole } from '../auth/api/auth.hook';
import { selectMitarbeiterId } from '../auth/api/authSlice';
import { MitarbeiterRole } from '../auth/api/types';

const DASHBOARD_MONTH_COUNT = 13;

export const DashboardAufwaendeStats: FC = () => {
  const [mode, setMode] = useState<'aufwaende' | 'stunden'>('stunden');
  const [useFindAllAufwand] = useLazyFindAllAufwandQuery();
  const mitarbeiterRole = useMitarbeiterRole();
  const mitarbeiterId = useAppSelector(selectMitarbeiterId);
  const [statsByMonth, setStatsByMonth] = useState<
    Record<
      string,
      {
        aufwaende: number;
        stundenFakturierbar: number;
        stundenNichtFakturierbar: number;
      }
    >
  >({});
  const [statsLoading, setStatsLoading] = useState(false);
  const isInternScope = mitarbeiterRole === MitarbeiterRole.INTERN;
  const scopeLabel = isInternScope ? 'Meine Aufwände' : 'Alle Aufwände';

  type MonthRange = {
    key: string;
    from: string;
    to: string;
    name: string;
  };

  type ChartItem = {
    name: string;
    aufwaende: number;
    stundenFakturierbar: number;
    stundenNichtFakturierbar: number;
  };

  function getMonthRange(baseDate: Date, offset: number): MonthRange {
    const date = new Date(baseDate.getFullYear(), baseDate.getMonth() - offset, 1);
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const monthEnd = endOfMonth(monthStart);

    return {
      key: monthStart.toISOString(),
      from: monthStart.toISOString(),
      to: monthEnd.toISOString(),
      name: monthStart.toLocaleDateString('de-DE', {
        month: 'short',
        year: '2-digit',
      }),
    };
  }

  const months: MonthRange[] = useMemo(() => {
    const today = new Date();
    return Array.from({ length: DASHBOARD_MONTH_COUNT }, (_, index) =>
      getMonthRange(today, DASHBOARD_MONTH_COUNT - 1 - index)
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadMonthlyStats() {
      setStatsLoading(true);
      const nextStatsByMonth: Record<
        string,
        {
          aufwaende: number;
          stundenFakturierbar: number;
          stundenNichtFakturierbar: number;
        }
      > = {};

      const roleFilterActive = mitarbeiterRole === MitarbeiterRole.INTERN && Boolean(mitarbeiterId);

      for (const month of months) {
        let page = 0;
        const pageSize = 500;
        let fetched = 0;
        let total = 0;
        let aufwaendeCount = 0;
        let fakturierbarMillis = 0;
        let nichtFakturierbarMillis = 0;

        do {
          try {
            const filter: {
              datum: {
                operator: 'between';
                from: string;
                to: string;
              };
              mitarbeiterId?: {
                operator: 'equal';
                value: string;
              };
            } = {
              datum: {
                operator: 'between',
                from: month.from,
                to: month.to,
              },
            };

            if (roleFilterActive && mitarbeiterId) {
              filter.mitarbeiterId = {
                operator: 'equal',
                value: mitarbeiterId,
              };
            }

            const result = await useFindAllAufwand({
              filter,
              pagination: {
                page,
                pageSize,
              },
              sort: { datum: 'asc' },
            }).unwrap();

            total = result.pagination.count;
            fetched += result.data.length;
            aufwaendeCount += result.data.length;

            result.data.forEach(aufwand => {
              if (aufwand.istFakturierbar) {
                fakturierbarMillis += aufwand.dauerInMillis;
              } else {
                nichtFakturierbarMillis += aufwand.dauerInMillis;
              }
            });

            page += 1;
          } catch {
            total = 0;
            fetched = 0;
            aufwaendeCount = 0;
            fakturierbarMillis = 0;
            nichtFakturierbarMillis = 0;
            break;
          }
        } while (fetched < total);

        nextStatsByMonth[month.key] = {
          aufwaende: aufwaendeCount,
          stundenFakturierbar: fakturierbarMillis / (1000 * 60 * 60),
          stundenNichtFakturierbar: nichtFakturierbarMillis / (1000 * 60 * 60),
        };
      }

      if (!cancelled) {
        setStatsByMonth(nextStatsByMonth);
        setStatsLoading(false);
      }
    }

    loadMonthlyStats();

    return () => {
      cancelled = true;
    };
  }, [mitarbeiterId, mitarbeiterRole, months, useFindAllAufwand]);

  const data: ChartItem[] = months.map(month => {
    const monthStats = statsByMonth[month.key] ?? {
      aufwaende: 0,
      stundenFakturierbar: 0,
      stundenNichtFakturierbar: 0,
    };

    return {
      name: month.name,
      aufwaende: monthStats.aufwaende,
      stundenFakturierbar: monthStats.stundenFakturierbar,
      stundenNichtFakturierbar: monthStats.stundenNichtFakturierbar,
    };
  });

  const isFetching = statsLoading;
  const monatHatDaten =
    mode === 'aufwaende'
      ? data.some(month => month.aufwaende > 0)
      : data.some(month => month.stundenFakturierbar > 0 || month.stundenNichtFakturierbar > 0);

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography
        variant="h6"
        sx={{
          color: 'text.secondary',
        }}
      >
        Statistik
      </Typography>
      <Card sx={{ width: '100%', height: 400 }}>
        <CardContent>
          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            <TransitionGroup appear={true}>
              <Box sx={{ position: 'absolute', top: 4, left: 8 }}>
                <Fade in={!monatHatDaten} timeout={!monatHatDaten ? 3000 : 300}>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Bisher keine Daten vorhanden
                  </Typography>
                </Fade>
              </Box>
              {monatHatDaten && (
                <Collapse timeout={1000} sx={{ mb: 2 }}>
                  <Box sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box>
                        <Typography variant="h6">
                          {mode === 'aufwaende'
                            ? `${scopeLabel} pro Monat`
                            : `${isInternScope ? 'Meine Stunden' : 'Alle Stunden'} pro Monat`}
                        </Typography>
                      </Box>

                      <ToggleButtonGroup
                        size="small"
                        color="primary"
                        value={mode}
                        exclusive
                        onChange={(_event, nextMode: 'aufwaende' | 'stunden' | null) => {
                          if (nextMode) {
                            setMode(nextMode);
                          }
                        }}
                      >
                        <ToggleButton value="stunden">Stunden</ToggleButton>
                        <ToggleButton value="aufwaende">Aufwände</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <RechartTabelle data={data} mode={mode} />
                  </Box>
                </Collapse>
              )}
            </TransitionGroup>
          </Box>
        </CardContent>
        <BetaChip size="small" sx={{ position: 'absolute', bottom: 8, right: 8 }} />
      </Card>
    </Box>
  );
};
